/**
 * ADR-20260621 slice 5: export-readiness coherence state handling.
 *
 * The orchestration-service export endpoint returns coherence metadata:
 *   X-Agion-Export-Coherence: complete | degraded | blocked
 *   X-Agion-Export-Unverified: true (when host could not be reached)
 *
 * This module provides a pure, testable helper to map response headers to
 * user-facing feedback, and the types for the enriched export result.
 */

/** Coherence state returned by the export endpoint. */
export type ExportCoherence = 'complete' | 'degraded' | 'blocked'

/** Headers parsed from a successful export response. */
export interface ExportCoherenceHeaders {
  coherence: ExportCoherence
  unverified: boolean
}

/** Result returned by exportMission after reading blob + coherence headers. */
export interface ExportMissionResult {
  blob: Blob
  filename: string
  coherence: ExportCoherence
  unverified: boolean
}

/**
 * The user-facing feedback to surface after an export completes.
 *
 * - silent: download succeeded with full coherence, no toast needed.
 * - warning: downloaded but degraded/unverified; show a non-blocking warning.
 */
export interface ExportFeedback {
  severity: 'silent' | 'warning'
  message?: string
}

/**
 * Parse export coherence headers from a Response.
 *
 * Defaults to `complete` if the header is absent (for back-compat with older
 * orchestration-service that does not emit the header).
 */
export function parseExportCoherenceHeaders(headers: Headers): ExportCoherenceHeaders {
  const raw = headers.get('x-agion-export-coherence')?.toLowerCase()
  let coherence: ExportCoherence = 'complete'
  if (raw === 'degraded' || raw === 'blocked') {
    coherence = raw
  }
  const unverified = headers.get('x-agion-export-unverified')?.toLowerCase() === 'true'
  return { coherence, unverified }
}

/**
 * Pure mapping from coherence state to user-facing feedback.
 *
 * Exported for unit testing so the UX decisions are captured in one place.
 */
export function getExportFeedback(
  coherence: ExportCoherence,
  unverified: boolean
): ExportFeedback {
  // blocked never reaches this path (422 throws); included for completeness
  if (coherence === 'blocked') {
    return {
      severity: 'warning',
      message:
        'Export blocked: the mission has unresolved bindings. Fix them and retry.',
    }
  }
  if (coherence === 'degraded' && unverified) {
    return {
      severity: 'warning',
      message:
        'Exported, but the package is degraded and unverified. Some operation contracts could not be embedded because the host was unreachable.',
    }
  }
  if (coherence === 'degraded') {
    return {
      severity: 'warning',
      message:
        'Exported, but the package is degraded. Some operation contracts could not be embedded and may need re-binding on import.',
    }
  }
  if (unverified) {
    return {
      severity: 'warning',
      message:
        'Exported, but unverified. The source host could not be reached to verify operation contracts; the package may not fully reconstruct.',
    }
  }
  // complete + verified → silent success
  return { severity: 'silent' }
}
